<template>
  <Popover
    :position="filterPopoverPosition"
    @on-active-change="onActiveChange"
    :data-e2e="TableCypressAttribute.buttons.FILTER"
    ref="popover"
  >
    <template #trigger>
      <ButtonView
        icon-left="cls-filter"
        icon-pack="cls"
        type="is-primary is-light"
        class="is-hidden-mobile"
        :data-e2e="TableCypressAttribute.filter.buttons.TRIGGER"
      >
        {{ filterButtonText }}
      </ButtonView>
    </template>
    <template #content>
      <ValidationObserver ref="validationObserver" v-slot="{ invalid }">
        <section class="content p-3" :class="{ 'has-fixed-width': hasInBetweenFilter }">
          <div v-for="(filter, index) in selectedTableFilters" :key="filter.id">
            <hr v-if="index" class="has-background-grey-lightest m-b-20" />
            <div class="is-flex align-items-center is-justify-content-space-between">
              <div class="is-flex filter-inputs">
                <p class="prefix is-uppercase has-text-weight-semibold is-size-6">
                  <span v-if="index === 0">{{ $t('tablefilter.where') }}</span>
                  <span v-else>{{ $t('core.and') }}</span>
                </p>
                <div>
                  <FormMultiSelect
                    v-model="filter.columnField"
                    :config="columnsConfig"
                    :show-float-label="false"
                    @select="(column) => onColumnSelect(column, filter)"
                    :rules="{ required: true }"
                    :data-e2e="TableCypressAttribute.filter.inputs.COLUMNS"
                    class="column-select"
                  />
                </div>
                <div v-if="filter.columnField">
                  <TableFilterComponent :table-filter="filter" />
                </div>
              </div>
              <div class="my-auto">
                <i
                  class="cls cls-remove is-clickable m-l-15 m-r-10 is-size-6 is-relative m-b-15"
                  @click.stop="onRemove(filter)"
                  :data-e2e="TableCypressAttribute.filter.buttons.REMOVE"
                ></i>
              </div>
            </div>
          </div>
          <div class="is-flex is-justify-content-space-between">
            <div>
              <span
                class="has-text-weight-semibold is-link m-l-5 m-t-3"
                :class="{ 'is-disabled has-text-grey-light': invalid || areAllFiltersApplied }"
                @click="addFilter"
                :data-e2e="TableCypressAttribute.filter.buttons.ADD_FILTER"
              >
                {{ $t('+ Add filter') }}
              </span>
            </div>
            <ButtonView
              v-if="!invalid"
              class="m-t-5"
              type="is-primary"
              @click="onApplyClicked"
              :data-e2e="TableCypressAttribute.filter.buttons.APPLY"
            >
              <span>{{ $t('core.apply') }}</span>
            </ButtonView>
          </div>
        </section>
      </ValidationObserver>
    </template>
  </Popover>
</template>

<script>
import ValidationObserver from '/@/modules/core/form/components/ValidationObserver.vue';
import Popover from '/@/modules/core/ui/components/Popover.vue';
import TableFilterComponent from '/@/modules/core/table/components/TableFilter.vue';
import { reject } from 'lodash';
import { BaseListService } from '/@/modules/core/table/services/BaseListService';
import { TableReloadMixin } from '/@/modules/core/table/mixins/TableReloadMixin';
import { TableFilter } from '/@/modules/core/table/models/TableFilter';
import { FormSelectConfig } from '/@/modules/core/form/models/FormSelectConfig';
import FormMultiSelect from '/@/modules/core/form/components/FormMultiSelect.vue';
import { TableCypressAttribute } from '/@/modules/core/table/constants/TableCypressAttribute';
import { TableParamCache } from '/@/modules/core/table/services/TableParamCache';
import { BaseColumns } from '/@/modules/core/table/models/BaseColumns';
import { UrlQueryOperator } from '/@/modules/core/app/constants/UrlQueryOperator';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'TableFilterPopOver',
  emits: ['on-apply-clicked'],
  mixins: [TableReloadMixin],
  components: {
    ButtonView,
    FormMultiSelect,
    TableFilterComponent,
    Popover,
    ValidationObserver
  },
  props: {
    columns: {
      type: BaseColumns,
      required: true
    },
    dataService: {
      type: BaseListService
    },
    predefinedData: {
      type: [Array, Object]
    },
    tableId: {
      type: String,
      required: false
    },
    canCacheParams: {
      type: Boolean,
      default: false
    },
    filterPopoverPosition: {
      type: String,
      default: 'is-bottom-right'
    },
    buttonLabel: {
      type: String,
      default: null
    }
  },
  static: {
    TableCypressAttribute
  },
  data() {
    return {
      /** @var TableFilter[] */
      selectedTableFilters: [],
      filterableColumns: null,
      tableParamCache: new TableParamCache(this.tableId, this.columns)
    };
  },
  computed: {
    hasInBetweenFilter() {
      return this.selectedTableFilters.some(
        (filter) => filter.operator === UrlQueryOperator.BETWEEN.key
      );
    },
    filterButtonText() {
      const filters = this.selectedTableFilters.filter((filter) => !!filter.column);
      const label = this.buttonLabel || this.$t('core.filter');
      return label + (filters.length ? `: ${filters.length}` : '');
    },
    areFiltersValid() {
      return !this.$refs.validationObserver?.flags.invalid;
    },
    columnsConfig() {
      const disabledValues = this.selectedTableFilters.map((filter) => filter.columnField);
      return new FormSelectConfig({
        options: this.filterableColumns,
        trackBy: 'field',
        label: 'label',
        disabledValues
      });
    },
    areAllFiltersApplied() {
      return this.selectedTableFilters.length === this.filterableColumns.length;
    }
  },
  methods: {
    addFilter() {
      this.selectedTableFilters.push(new TableFilter());
    },
    onActiveChange(isShowing) {
      if (isShowing) {
        if (!this.selectedTableFilters.length) {
          this.addFilter();
        }
        return;
      }

      if (this.areFiltersValid && this.hasChangedFilters()) {
        this.applyFilters();
      }
    },
    applyFilters() {
      this.removeAllFilterableColumns();
      this.applyFilterableColumnsFromSelection();
      this.updateOriginalFilters();
    },
    onApplyClicked() {
      this.$refs.popover.toggle();
      this.applyFilters();
      this.reloadTable();
      this.$emit('on-apply-clicked', this.selectedTableFilters);
      this.tableParamCache.selectedTableFilters = this.selectedTableFilters;
    },
    /**
     * @param {TableFilter} filter
     */
    onRemove(filter) {
      this.selectedTableFilters = reject(
        this.selectedTableFilters,
        (tableFilter) => tableFilter.column?.field === filter.column?.field
      );
    },
    removeAllFilterableColumns() {
      Object.values(this.columns).forEach((column) => {
        if (column.isFilterable && this.dataService) {
          this.dataService.unsetFilterParam(column.filterParam);
        }
      });
    },
    applyFilterableColumnsFromSelection() {
      this.selectedTableFilters.forEach((tableFilter) => {
        if (tableFilter.column && this.dataService) {
          this.dataService.setFilterParam(
            tableFilter.column.filterParam,
            tableFilter.calculateValue()
          );
        }
      });
    },
    /**
     * @param {TableColumn} column
     * @param {TableFilter} filter
     */
    onColumnSelect(column, filter) {
      filter.columnField = column.field;
      filter.setColumn(column);
    },
    updateOriginalFilters() {
      this.originalFilters = JSON.stringify(this.selectedTableFilters);
    },
    hasChangedFilters() {
      return this.originalFilters !== JSON.stringify(this.selectedTableFilters);
    },
    applyCachedParams() {
      this.selectedTableFilters = this.tableParamCache.selectedTableFilters;
    }
  },
  created() {
    this.filterableColumns = Object.values(this.columns ?? []).filter(
      (column) => column.isFilterable
    );

    if (this.canCacheParams && this.tableParamCache.selectedTableFilters.length) {
      this.applyCachedParams();
      this.applyFilters();
    }

    if (!this.selectedTableFilters.length) {
      this.addFilter();
    }

    this.updateOriginalFilters();
  }
};
</script>

<style scoped>
.prefix {
  position: relative;
  top: var(--ce-8px);
  text-align: right;
  width: var(--ce-50px);
  margin-right: var(--ce-8px);
}

.remove-button {
  position: relative;
  top: var(--ce-10px);
}

section.content {
  min-width: var(--ce-350px);
}

.has-fixed-width {
  width: 50vw;

  .filter-inputs {
    flex-wrap: wrap;
  }
}

section :deep(section.form-multi-select) {
  width: var(--ce-100px);
  margin-bottom: var(--ce-10px) !important;
}

section :deep(section.form-multi-select.column-select) {
  width: var(--ce-175px);
}

section :deep(section.form-multi-select.value-select) {
  width: var(--ce-250px);
}

section :deep(ul.multiselect__content) {
  margin-top: unset;
  margin-left: unset;
}

section :deep(input) {
  width: var(--ce-180px);
}

section :deep(input[type='number']) {
  height: var(--ce-39px) !important;
  padding: 0 0 0 var(--ce-18px) !important;
}

section :deep(.is-split input) {
  width: var(--ce-120px);
}

section :deep(.datepicker input) {
  height: var(--ce-35px) !important;
  padding: var(--ce-18-5px) var(--ce-18-5px) var(--ce-18-5px) var(--ce-35px) !important;
  width: max-content !important;
}

section :deep(.multiselect__option--group) {
  text-transform: inherit !important;
  font-size: inherit !important;
}
</style>
